import { useMutation, useQueryClient } from "react-query";
import {
  BeneSottoTipologiaModel,
  BeneSottoTipologiaPostModel,
  BeneSottoTipologiaPutModel,
  BeneSottoTipologiaRequest,
} from "../../../models/api/response/bene/BeneSottoTipologiaModel";
import {
  WithCUToggleCommandsAndData,
  WithData,
  WithRecordID,
} from "../../../models/shared/CommonModels";
import { useState } from "react";
import { fetchSottoTipologiaBeneSelectQueryKey } from "../../../utils/QueryClient";
import {
  createSottoTipologiaBene,
  updateSottoTipologia,
} from "../../../api/bene/sottotipologia";
import {
  fromAxiosErrorToMessage,
  prepareCreateData,
  prepareEditData,
} from "../../../utils/crud";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  DialogActions,
  Box,
  Button,
} from "@mui/material";
import { AxiosError } from "axios";
import { REQUIRED_HELPER_TEXT } from "../../../utils/form";
import ErrorMessageAlertComponent from "../../errors/ErrorMessageAlertComponent";

const BeneSottoTipologiaFormComponent: React.FC<
  WithCUToggleCommandsAndData<BeneSottoTipologiaModel>
> = ({ editEnabled, onClose, initialData, open, toggleOpen }) => {
  const queryClient = useQueryClient();

  const [hideError, setHideError] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [nome, setNome] = useState<string>(initialData.nome || "");
  const [descrizione, setDescrizione] = useState<string>(
    initialData.descrizione || ""
  );

  const [error, setError] = useState<boolean>(false);

  const resetFields = () => {
    setNome("");
    setDescrizione("");
    setHideError(true);
  };

  const createMutation = useMutation({
    mutationFn: (x: WithData) => createSottoTipologiaBene(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchSottoTipologiaBeneSelectQueryKey);
      resetFields();
      if (onClose !== undefined) onClose();
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const editMutation = useMutation({
    mutationFn: (x: WithRecordID) => updateSottoTipologia(x),
    onSuccess: () => {
      queryClient.invalidateQueries(fetchSottoTipologiaBeneSelectQueryKey);
    },
    onError: (err: AxiosError) => {
      setHideError(false);
      setErrorMessage(fromAxiosErrorToMessage(err));
    },
  });

  const closeActions = () => {
    toggleOpen();
    if (!hideError) {
      queryClient.invalidateQueries(fetchSottoTipologiaBeneSelectQueryKey);
    }
  };

  const handleSubmit = () => {
    if (editEnabled) {
      if (initialData === undefined) {
        return;
      }

      const benePut = {} as BeneSottoTipologiaPutModel;
      benePut.nome = nome;
      benePut.descrizione = descrizione;
      benePut.id_tipologia_bene = initialData.id_tipologia_bene;

      const request = {} as BeneSottoTipologiaRequest;
      request.sotto_tipologia_bene = benePut;

      editMutation.mutate(prepareEditData(initialData.id, request));
    } else {
      const benePost = {} as BeneSottoTipologiaPostModel;
      benePost.nome = nome;
      benePost.descrizione = descrizione;
      benePost.id_tipologia_bene = initialData.id_tipologia_bene;

      const request = {} as BeneSottoTipologiaRequest;
      request.sotto_tipologia_bene = benePost;

      createMutation.mutate(prepareCreateData(request));
    }

    toggleOpen();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={closeActions}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        }}
      >
        <DialogTitle>
          {editEnabled ? "Modifica" : "Crea Nuova"} Sotto-Tipologia Bene
        </DialogTitle>
        <DialogContent style={{ width: 500 }}>
          <ErrorMessageAlertComponent
            error_message={errorMessage}
            hide={() => {
              setHideError(true);
            }}
            visible={!hideError}
          />

          <Typography sx={{ fontSize: "12px", color: "grey" }}>
            {REQUIRED_HELPER_TEXT}
          </Typography>

          <TextField
            error={error ? error : undefined}
            margin="normal"
            required={true}
            label="Nome"
            name="nome"
            multiline
            fullWidth
            value={nome}
            onChange={(e) => {
              setError(false);
              setNome(e.target.value);
            }}
          />

          <TextField
            error={error ? error : undefined}
            margin="normal"
            required={false}
            label="Descrizione"
            name="descrizione"
            multiline
            fullWidth
            value={descrizione}
            onChange={(e) => {
              setError(false);
              setDescrizione(e.target.value);
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            paddingLeft: "18pt",
            paddingRight: "18pt",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ justifyContent: "flex-start" }}>
            <Button
              onClick={() => {
                if (onClose !== undefined) {
                  onClose();
                }

                if (!editEnabled) {
                  resetFields();
                }

                closeActions();
              }}
            >
              Chiudi
            </Button>
          </Box>
          <Box sx={{ justifyContent: "flex-end" }}>
            <Button onClick={() => handleSubmit()} variant="contained">
              {editEnabled ? "Salva" : "Crea"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BeneSottoTipologiaFormComponent;
